import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { useLang } from "../contexts/LangContext";
import { ArrowRight, MapPin, ArrowLeftRight, ChevronDown } from "lucide-react";
import { getFlag } from "../lib/currencyFlags";

const CurrencySelect = ({ value, onChange, currencies, lang, label }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = currencies.find((c) => c.code === value) || currencies[0];

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <label className="block text-[10px] uppercase tracking-overline text-white/40 mb-2">{label}</label>

      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center gap-2.5 px-3.5 py-3 text-white text-sm focus:outline-none transition-all rounded-sm
          ${open
            ? "bg-white/20 border border-coral shadow-[0_0_0_1px_rgba(232,96,60,0.3)]"
            : "bg-white/8 border border-white/15 hover:bg-white/15 hover:border-white/30"
          }`}
        style={{ background: open ? "rgba(255,255,255,0.13)" : "rgba(255,255,255,0.07)" }}
      >
        {getFlag(selected.code)
          ? <img src={getFlag(selected.code)} alt={selected.code} className="w-6 h-4 object-cover rounded-[2px] shrink-0 shadow" />
          : <span className="w-6 h-4 bg-white/20 rounded-[2px] shrink-0" />
        }
        <span className="font-display font-bold text-base leading-none">{selected.code}</span>
        <span className="text-white/45 text-xs truncate hidden sm:block leading-none">
          {lang === "en" ? selected.name_en : selected.name_ro}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 ml-auto shrink-0 text-white/50 transition-transform duration-200 ${open ? "rotate-180 text-coral" : ""}`} />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 top-[calc(100%+4px)] left-0 right-0 rounded-sm overflow-hidden shadow-2xl border border-white/10"
          style={{ background: "linear-gradient(135deg, #2a0a04 0%, #1e0604 100%)" }}
        >
          <div className="max-h-56 overflow-y-auto">
            {currencies.map((c) => {
              const isActive = c.code === value;
              return (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => { onChange(c.code); setOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3.5 py-2.5 text-sm text-left transition-colors
                    ${isActive
                      ? "bg-coral/20 border-l-2 border-coral"
                      : "border-l-2 border-transparent hover:bg-white/8"
                    }`}
                >
                  {getFlag(c.code)
                    ? <img src={getFlag(c.code)} alt={c.code} className="w-6 h-4 object-cover rounded-[2px] shrink-0 shadow" />
                    : <span className="w-6 h-4 bg-white/20 rounded-[2px] shrink-0" />
                  }
                  <span className={`font-display font-bold text-sm w-10 shrink-0 ${isActive ? "text-coral" : "text-white"}`}>{c.code}</span>
                  <span className="text-white/40 text-xs truncate">{lang === "en" ? c.name_en : c.name_ro}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const RON = { code: "RON", name_ro: "leu romanesc", name_en: "Romanian leu", unit: 1, buy: 1, sell: 1 };

const Hero = ({ topRates = [], rates = [] }) => {
  const { t, lang } = useLang();

  const allCurrencies = useMemo(() => [RON, ...rates], [rates]);
  const [from, setFrom] = useState("EUR");
  const [to, setTo] = useState("RON");
  const [amount, setAmount] = useState("100");

  const findCur = useCallback(
    (code) => allCurrencies.find((c) => c.code === code) || RON,
    [allCurrencies]
  );

  const result = useMemo(() => {
    const a = parseFloat((amount || "").toString().replace(",", ".")) || 0;
    if (a <= 0) return 0;
    const f = findCur(from);
    const tCur = findCur(to);
    let ron;
    if (f.code === "RON") {
      ron = a;
    } else {
      ron = (a / (f.unit || 1)) * (f.buy || 0);
    }
    let out;
    if (tCur.code === "RON") {
      out = ron;
    } else {
      out = ron / ((tCur.sell || 1) / (tCur.unit || 1));
    }
    return out;
  }, [amount, from, to, findCur]);

  const rateLabel = useMemo(() => {
    const f = findCur(from);
    const tCur = findCur(to);
    if (from === "RON" && to !== "RON") {
      return `1 ${to} = ${((tCur.sell || 0) / (tCur.unit || 1)).toFixed(4)} RON`;
    }
    if (from !== "RON" && to === "RON") {
      return `1 ${from} = ${((f.buy || 0) / (f.unit || 1)).toFixed(4)} RON`;
    }
    if (from !== "RON" && to !== "RON") {
      return `1 ${from} = ${((f.buy || 0) / (f.unit || 1)).toFixed(4)} RON · 1 ${to} = ${((tCur.sell || 0) / (tCur.unit || 1)).toFixed(4)} RON`;
    }
    return null;
  }, [from, to, findCur]);

  const swap = () => { setFrom(to); setTo(from); };

  return (
    <section className="relative bg-burgundy text-white overflow-hidden" id="hero-section" data-testid="hero-section">
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1571663106504-915de83a8ed9?w=1600&q=80)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(105deg, rgba(90,26,20,0.95) 0%, rgba(90,26,20,0.85) 45%, rgba(90,26,20,0.55) 100%)",
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none mix-blend-overlay opacity-60"
        style={{
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "3px 3px",
        }}
      />

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 pt-20 pb-28 lg:pt-28 lg:pb-36 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <div className="flex items-center gap-3 mb-8" data-testid="hero-since">
            <span className="live-dot" />
            <span className="text-[10px] uppercase tracking-overline text-coral font-medium">
              {t.hero.live}
            </span>
            <span className="hidden md:block w-12 h-px bg-white/30" />
            <span className="hidden md:inline text-[10px] uppercase tracking-overline text-white/60">
              {t.hero.since} · Constanta
            </span>
          </div>

          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-7xl leading-[0.95]">
            {t.hero.title_1}
            <br />
            <span className="text-white/80">{t.hero.title_2}</span>
            <br />
            <span className="italic text-coral">{t.hero.title_3}</span>
          </h1>

          <p className="mt-8 max-w-xl text-base lg:text-lg text-white/75 font-light leading-relaxed">
            {t.hero.subtitle}
          </p>

          <div className="mt-10 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-4">
            <a
              href="#rates"
              className="btn-coral inline-flex items-center justify-center gap-2 px-6 py-3.5 text-xs uppercase tracking-overline font-medium border border-transparent w-full sm:w-auto"
              data-testid="hero-cta-rates"
            >
              {t.hero.cta_rates} <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="/locatii"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-xs uppercase tracking-overline font-medium border border-white/30 hover:border-coral hover:text-coral transition-colors w-full sm:w-auto"
              data-testid="hero-cta-locations"
            >
              <MapPin className="w-4 h-4" /> {t.hero.cta_locations}
            </a>
          </div>
        </div>

        <div className="lg:col-span-6">
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 p-6 lg:p-8">
            <div className="text-[10px] uppercase tracking-overline text-coral mb-5">{t.converter.kicker}</div>

            <div className="mb-5">
              <label className="block text-[10px] uppercase tracking-overline text-white/50 mb-2">
                {t.converter.amount}
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full font-mono-num text-3xl bg-transparent border-b-2 border-white/40 focus:outline-none focus:border-coral pb-2 text-white"
                data-testid="hero-converter-amount"
              />
            </div>

            <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-end mb-6">
              <CurrencySelect
                value={from}
                onChange={setFrom}
                currencies={allCurrencies}
                lang={lang}
                label={t.converter.from}
              />
              <button
                type="button"
                onClick={swap}
                className="p-2.5 border border-white/20 hover:border-coral hover:text-coral transition-colors"
                aria-label="swap"
              >
                <ArrowLeftRight className="w-4 h-4" />
              </button>
              <CurrencySelect
                value={to}
                onChange={setTo}
                currencies={allCurrencies}
                lang={lang}
                label={t.converter.to}
              />
            </div>

            <div className="pt-5 border-t border-white/15">
              <div className="text-[10px] uppercase tracking-overline text-white/50 mb-1">
                {t.converter.get}
              </div>
              <div className="font-mono-num text-coral text-4xl font-semibold" data-testid="hero-converter-result">
                {result.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}{" "}
                <span className="font-display text-2xl text-white/60 ml-1">{to}</span>
              </div>
              {rateLabel && (
                <div className="text-[10px] uppercase tracking-overline text-white/40 mt-2">
                  {rateLabel}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Live ticker */}
      {topRates.length > 0 && (
        <div className="relative border-t border-white/10 bg-[#481512]" data-testid="hero-ticker">
          <div className="overflow-hidden">
            <div className="flex animate-marquee whitespace-nowrap py-3" style={{ width: "200%" }}>
              {[...topRates, ...topRates, ...topRates, ...topRates].map((r, i) => (
                <div key={i} className="flex items-center gap-3 mx-8 text-sm">
                  <span className="font-mono-num text-coral text-xs">{r.unit}</span>
                  <span className="text-[10px] tracking-overline uppercase text-white/60">{r.code}</span>
                  <span className="font-mono-num">{r.buy.toFixed(4)}</span>
                  <span className="text-white/30">/</span>
                  <span className="font-mono-num">{r.sell.toFixed(4)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Hero;
